/* eslint-disable tsdoc/syntax */
/* eslint-disable no-extend-native */
import { OPERATION_ID_SHOWING_NUMBER_LENGTH } from "@zenith/data/constants";
import {
  OperationBaseDTO,
  OperationDTO,
  OperationFullDTO,
  PaymentChargedDTO,
  PaymentLinkDTO,
  TransactionBaseDTO,
  TransactionFullDTO,
} from "@zenith/services/transactions";
import {
  BusinessAddressDTO,
  BusinessDetailsResponseDTO,
  DirectorDetailsStepStatusDTO,
  DocumentStatusResponseDTO,
  NINVerificationResponseDTO,
  SelfieStatusResponseDTO,
  StartUpgradeTierRequestDTO,
  TierStepsResponseDTO,
} from "@zenith/services/user";

declare global {
  interface String {
    formatToShowID(): string;
    mapPaymentMethod(): string;
    mapStatus(): string;
    mapUpgradeTier(tier: string): string;
    mapUpgradeStatus(): TierStepsResponseDTO.stepStatus;
    getCardType(): string;
  }
}

String.prototype.formatToShowID = function (): string {
  if (!this) {
    return "";
  }
  return this.slice(OPERATION_ID_SHOWING_NUMBER_LENGTH);
};

String.prototype.mapPaymentMethod = function (): string {
  if (!this) {
    return "";
  }

  const value = this.toString();

  const newerPaymentMethods: Record<string, string> = {
    MANUAL_CREDIT: "Manual Credit",
    MANUAL_DEBIT: "Manual Debit",
    OUTLET_WITHDRAWAL: "Outlet Withdrawal",
    OUTLET_AUTO_SWEEP: "Outlet Auto Sweep",
    TRANSACTION_CHARGE: "Transaction Charge",
  };
  if (newerPaymentMethods[value]) {
    return newerPaymentMethods[value];
  }

  if (value === "POS_ORDER") {
    return "POS Order";
  }
  if (value === "PAYMENT_LINK_PAY_WITH_TRANSFER") {
    return "Payment Link - Pay with Transfer";
  }
  if (value === "INWARDS_TRANSFER") {
    return "Inwards Transfer";
  }
  if (value === "PAY_WITH_TRANSFER") {
    return "Pay with Transfer";
  }
  if (value === "PAYMENT_LINK_USSD") {
    return "Payment Link - USSD";
  }
  if (value === "PAYMENT_LINK_CARD") {
    return "Payment Link - Card";
  }
  if (value === "PAYMENT_LINK_QR_CODE") {
    return "Payment Link - QR Code";
  }

  switch (this) {
    case OperationDTO.paymentMethod.CARD:
    case PaymentChargedDTO.paymentMethod.CARD:
    case OperationBaseDTO.paymentMethod.CARD:
    case OperationFullDTO.paymentMethod.CARD:
    case TransactionFullDTO.paymentMethod.CARD:
      return "Card";

    case OperationDTO.paymentMethod.CASH:
    case PaymentChargedDTO.paymentMethod.CASH:
    case OperationBaseDTO.paymentMethod.CASH:
    case OperationFullDTO.paymentMethod.CASH:
    case TransactionFullDTO.paymentMethod.CASH:
      return "Cash";

    case OperationFullDTO.paymentMethod.INWARDS_TRANSFER:
    case TransactionFullDTO.paymentMethod.INWARDS_TRANSFER:
    case TransactionBaseDTO.paymentMethod.INWARDS_TRANSFER:
      return "Inwards Transfer";

    case OperationFullDTO.paymentMethod.PAY_WITH_TRANSFER:
    case TransactionFullDTO.paymentMethod.PAY_WITH_TRANSFER:
    case TransactionBaseDTO.paymentMethod.PAY_WITH_TRANSFER:
      return "Pay with Transfer";

    case OperationFullDTO.paymentMethod.POS_ORDER:
    case TransactionFullDTO.paymentMethod.POS_ORDER:
    case TransactionBaseDTO.paymentMethod.POS_ORDER:
      return "POS Order";

    case OperationFullDTO.paymentMethod.PAYMENT_LINK_PAY_WITH_TRANSFER:
    case TransactionFullDTO.paymentMethod.PAYMENT_LINK_PAY_WITH_TRANSFER:
    case TransactionBaseDTO.paymentMethod.PAYMENT_LINK_PAY_WITH_TRANSFER:
      return "Payment Link - Pay with Transfer";

    case OperationFullDTO.paymentMethod.PAYMENT_LINK_USSD:
    case TransactionFullDTO.paymentMethod.PAYMENT_LINK_USSD:
    case TransactionBaseDTO.paymentMethod.PAYMENT_LINK_USSD:
      return "Payment Link - USSD";

    case OperationFullDTO.paymentMethod.PAYMENT_LINK_CARD:
    case TransactionFullDTO.paymentMethod.PAYMENT_LINK_CARD:
    case TransactionBaseDTO.paymentMethod.PAYMENT_LINK_CARD:
      return "Payment Link - Card";

    case OperationFullDTO.paymentMethod.PAYMENT_LINK_QR_CODE:
    case TransactionFullDTO.paymentMethod.PAYMENT_LINK_QR_CODE:
    case TransactionBaseDTO.paymentMethod.PAYMENT_LINK_QR_CODE:
      return "Payment Link - QR Code";

    case OperationDTO.paymentMethod.MOMO:
    case PaymentChargedDTO.paymentMethod.MOMO:
    case TransactionBaseDTO.paymentMethod.MOMO:
    case OperationBaseDTO.paymentMethod.MOMO:
    case OperationFullDTO.paymentMethod.MOMO:
    case TransactionFullDTO.paymentMethod.MOMO:
      return "MoMo";

    case OperationDTO.paymentMethod.OUT_BANK_TRANSFER:
    case PaymentChargedDTO.paymentMethod.OUT_BANK_TRANSFER:
    case TransactionBaseDTO.paymentMethod.OUT_BANK_TRANSFER:
    case OperationBaseDTO.paymentMethod.OUT_BANK_TRANSFER:
    case OperationFullDTO.paymentMethod.OUT_BANK_TRANSFER:
    case TransactionFullDTO.paymentMethod.OUT_BANK_TRANSFER:
      return "Funds Transfer";

    case OperationDTO.paymentMethod.PAYMENT_LINK:
    case PaymentChargedDTO.paymentMethod.PAYMENT_LINK:
    case OperationBaseDTO.paymentMethod.PAYMENT_LINK:
    case OperationFullDTO.paymentMethod.PAYMENT_LINK:
    case TransactionFullDTO.paymentMethod.PAYMENT_LINK:
      return "Payment Link";

    case OperationDTO.paymentMethod.POS:
    case PaymentChargedDTO.paymentMethod.POS:
    case TransactionBaseDTO.paymentMethod.POS:
    case OperationBaseDTO.paymentMethod.POS:
    case OperationFullDTO.paymentMethod.POS:
    case TransactionFullDTO.paymentMethod.POS:
      return "POS";

    case OperationDTO.paymentMethod.QR_CODE:
    case PaymentChargedDTO.paymentMethod.QR_CODE:
    case TransactionBaseDTO.paymentMethod.QR_CODE:
    case OperationBaseDTO.paymentMethod.QR_CODE:
    case OperationFullDTO.paymentMethod.QR_CODE:
    case TransactionFullDTO.paymentMethod.QR_CODE:
      return "QR Code";

    case OperationDTO.paymentMethod.REFUND:
    case PaymentChargedDTO.paymentMethod.REFUND:
    case TransactionBaseDTO.paymentMethod.REFUND:
    case OperationBaseDTO.paymentMethod.REFUND:
    case OperationFullDTO.paymentMethod.REFUND:
    case TransactionFullDTO.paymentMethod.REFUND:
      return "Refund";

    case OperationDTO.paymentMethod.USSD:
    case PaymentChargedDTO.paymentMethod.USSD:
    case TransactionBaseDTO.paymentMethod.USSD:
    case OperationBaseDTO.paymentMethod.USSD:
    case OperationFullDTO.paymentMethod.USSD:
    case TransactionFullDTO.paymentMethod.USSD:
      return "USSD";

    case TransactionBaseDTO.paymentMethod.TRANSACTION_CHARGE:
      return "Transaction Charge";

    case TransactionBaseDTO.paymentMethod.STAMP_DUTY:
      return "Stamp Duty";

    case OperationDTO.paymentMethod.IN_BANK_TRANSFER:
      return "Pay with Transfer";

    case OperationDTO.paymentMethod.STATIC_ACCOUNT:
      return "Static Account";

    default:
      return "Unknown Payment Method";
  }
};

String.prototype.mapStatus = function (): string {
  if (!this) {
    return "";
  }

  switch (this) {
    case OperationDTO.status.DECLINED:
      return "Declined";
    case OperationDTO.status.EXPIRED:
      return "Expired";
    case OperationDTO.status.PENDING:
      return "Pending";
    case OperationDTO.status.SUCCESS:
      return "Success";
    case OperationDTO.status.FAILED:
      return "Failed";
    case OperationDTO.status.REVERSED:
      return "Reversed";
    default:
      return "Status not mapped.";
  }
};

String.prototype.mapUpgradeTier = function (
  tier: StartUpgradeTierRequestDTO.tierType
): string {
  if (!this) {
    return "";
  }

  switch (tier) {
    case StartUpgradeTierRequestDTO.tierType.BRONZE:
      return `${this} ${StartUpgradeTierRequestDTO.tierType.BRONZE}.`;
    case StartUpgradeTierRequestDTO.tierType.SILVER:
      return `${this} ${StartUpgradeTierRequestDTO.tierType.SILVER}.`;
    case StartUpgradeTierRequestDTO.tierType.GOLD:
      return `${this} ${StartUpgradeTierRequestDTO.tierType.GOLD}.`;
    case StartUpgradeTierRequestDTO.tierType.PLATINUM:
      return `${this} ${StartUpgradeTierRequestDTO.tierType.PLATINUM}.`;
    default:
      return "Tier is not mapped";
  }
};

String.prototype.mapUpgradeStatus =
  function (): TierStepsResponseDTO.stepStatus {
    if (!this) {
      return TierStepsResponseDTO.stepStatus.NOT_STARTED;
    }

    switch (this) {
      case SelfieStatusResponseDTO.status.VERIFIED_SUCCESSFULLY:
      case NINVerificationResponseDTO.verificationStatus.SUCCESS:
      case DocumentStatusResponseDTO.status.VERIFIED_SUCCESSFULLY:
      case "SUCCESS":
      case BusinessDetailsResponseDTO.verificationStatus.SUCCESS:
      case BusinessAddressDTO.verificationStatus.SUCCESS:
      case DirectorDetailsStepStatusDTO.upgradeTierStepStatus.APPROVED:
        return TierStepsResponseDTO.stepStatus.APPROVED;

      case SelfieStatusResponseDTO.status.PENDING_VERIFICATION:
      case NINVerificationResponseDTO.verificationStatus.PENDING:
      case DocumentStatusResponseDTO.status.PENDING_VERIFICATION:
      case "PENDING":
      case BusinessDetailsResponseDTO.verificationStatus.PENDING:
      case BusinessAddressDTO.verificationStatus.PENDING:
      case DirectorDetailsStepStatusDTO.upgradeTierStepStatus.PENDING:
        return TierStepsResponseDTO.stepStatus.PENDING;

      case SelfieStatusResponseDTO.status.ERROR_IN_VERIFICATION:
      case NINVerificationResponseDTO.verificationStatus.FAILED:
      case DocumentStatusResponseDTO.status.ERROR_IN_VERIFICATION:
      case "FAILED":
      case BusinessDetailsResponseDTO.verificationStatus.FAILED:
      case BusinessAddressDTO.verificationStatus.FAILED:
      case DirectorDetailsStepStatusDTO.upgradeTierStepStatus.REJECTED:
        return TierStepsResponseDTO.stepStatus.REJECTED;

      default:
        throw new Error("Status is not mapped");
    }
  };

String.prototype.getCardType = function (): string {
  if (!this) {
    return "Card";
  }
    const number = this.toString().trim().replace(/\s/g, "");
    if (!number) return "Card";
    const firstTwoDigits = parseInt(number.substring(0, 2));
  const firstFourDigits = parseInt(number.substring(0, 4));
    if (number.startsWith("4")) return "Visa";
    if ((firstTwoDigits >= 51 && firstTwoDigits <= 55) || 
      (firstFourDigits >= 2221 && firstFourDigits <= 2720)) {
    return "Mastercard";
  }
    if (number.startsWith("5060") || number.startsWith("5078") || 
      number.startsWith("5079") || number.startsWith("6500")) {
    return "Verve";
  }
    if (number.startsWith("5061")) return "Afrigo";
    return "Card";
};

export function mapPaymentMethodWithType(
  paymentMethod: string | undefined,
  partialTransactionType?: string
): string {
  if (!paymentMethod) return "Unknown";

  if (partialTransactionType?.toUpperCase() === "FEE") {
    return "Transaction Charge";
  }

  return paymentMethod.mapPaymentMethod();
}

export {};